import type { Firestore, PartialWithFieldValue } from 'firebase/firestore'

import { collection, doc, increment, serverTimestamp } from 'firebase/firestore'
import type { LocalTrack } from 'livekit-client'
import { type Room } from 'livekit-client'
import type { ObservableMap, ObservableSet } from 'mobx'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import type { ObservableCollection } from '../firestore-mobx'
import { updateDocWithError } from '../firestore-mobx/fetch'
import { type StaticModelCollection } from '../firestore-mobx/model'
import type {
  FirestoreRoomState,
  FirestoreRoomStateWrite,
} from '../firestore/RoomState'
import {
  roomStateAddHiddenUser,
  roomStateSetGroupLeaders,
  roomStateUpdatePumpSlide,
  updateRoomState,
} from '../firestore/RoomState'
import { ensureUserPeerReviewRecord } from '../firestore/RoomStateUPR'
import { deleteAllRoomStateCollaborationData } from '../firestore/RoomStateCollaborationData'
import { CollaborationDataType } from '../firestore/RoomStateCollaborationData/types'
import { createRoomStateFeedback } from '../firestore/RoomStateFeedback'
import { resetRoomState } from '../firestore/RoomStateReset'
import { SlideType } from '../firestore/Slide/types'
import type { FirestoreSlideDeckExhibit } from '../firestore/SlideDeckExhibit/schema'
import type { BreakoutUser } from '../models/BreakoutUser'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { PublicUser } from '../models/PublicUser'
import { RoomState } from '../models/RoomState'
import { RoomStateAnswer } from '../models/RoomStateAnswer'
import { RoomStateCollaborationData } from '../models/RoomStateCollaborationData'
import { RoomStateFeedback } from '../models/RoomStateFeedback'
import { RoomStateSummary } from '../models/RoomStateSummary'
import { Section } from '../models/Section'
import { SectionAssignment } from '../models/SectionAssignment'
import { SettingsProcessingVideo } from '../models/SettingsProcessingVideo'
import { SettingsDefaultProcessingAsset } from '../models/SettingsDefaultProcessingAsset'
import { Media } from '../models/Media'
import { SettingsSectionPassPricing } from '../models/SettingsSectionPassPricing'
import { SectionAssignmentPeerReviewQuestion } from '../models/SectionAssignmentPeerReviewQuestion'
import { SlideCaption } from '../models/SlideCaption'
import { SlideDeck } from '../models/SlideDeck'
import { SlideDeckExhibit } from '../models/SlideDeckExhibit'
import { SlideModel } from '../models/SlideModel'
import { SlideQuestion } from '../models/SlideQuestion'
import { SlideQuestionType } from '../models/SlideQuestionType'
import { UserProfileRoomToken } from '../models/UserProfileRoomToken'
import { sortQuestions } from '../sources-of-truth/sortQuestions'
import { ValidLibraryObject } from '../stores/ValidLibraryObject'
import {
  BroadcastState,
  ExhibitGridDisplayMode,
  SessionMuting,
  type MeetingSidebarTab,
} from '../types'
import { EventEmitter } from '../util/EventEmitter'
import { similarity } from '../util/arrays'
import { ServerIdentities } from '../util/identities'
import { Cubit } from './core'
import { DevModeController } from './meeting/DevModeController'
import { MeetingDataFetcher } from './meeting/MeetingDataFetcher'
import type { LivekitStreamStatus } from './meeting/MeetingLivekitController'
import { MeetingLivekitController } from './meeting/MeetingLivekitController'
import { MeetingQuestion } from './meeting/MeetingQuestion'
import { ModerationTasksController } from './meeting/ModerationTasksController'
import { SecretaryConnectionController } from './meeting/SecretaryConnectionController'
import { SequestrationController } from './meeting/SequestrationController'
import { SlideTimerController } from './meeting/SlideTimerController'
import { AvMediaManagementCubit } from './meeting/AvMediaManagementCubit'
import { EndOfSessionQuizSlide } from './meeting/slides/EndOfSessionQuizSlide'
import { GroupQuizSlide } from './meeting/slides/GroupQuizSlide'
import { InteractivePollSlide } from './meeting/slides/InteractivePollSlide'
import { InteractiveQuizSlide } from './meeting/slides/InteractiveQuizSlide'
import { MeetingResultsSlide } from './meeting/slides/MeetingResultsSlide'
import { Slide } from './meeting/slides/Slide'
import { SoloQuizSlide } from './meeting/slides/SoloQuizSlide'
import type { SlideDeckPreviewData } from './PreviewMeetingCubit'
import type { NextSlideButtonBlockReason } from '../types/meeting'
import { ClickWrap } from '../models/ClickWrap'
import { SettingsTermsAndConditions } from '../models/SettingsTermsAndConditions'
import { guardOnLoaded } from '../util/guardOn'
import { MeetingSimpleModerationCubit } from './MeetingSimpleModerationCubit'
import { SlideDeckRole } from '../models/SlideDeckRole'
import { MeetingExhibit } from '../models/MeetingExhibit'
import { doUsersSatisfyRoleRequirements } from '../sources-of-truth/doUsersSatisfyRoleRequirements'
import { MeetingChatController } from './meeting/MeetingChatController'

export { CollaborationDataType }

export type MeetingEvents = {
  slideChanged: []
  idleTimeout: []
  transcriptProblem: []
  transcriptSuccess: []
  pauseVideo: []
  dragPosition: [
    {
      id: string
      position: {
        x: number | undefined
        y: number | undefined
        anchor?: string
      }
      isDragged: boolean
      draggedBy: string | undefined
      label?: string | undefined
      userId?: string
      placeholder?: boolean
    },
  ]
  dragListPosition: [
    {
      id: string
      position: {
        idBefore: string | undefined
        containerId: string | undefined
        placeholder?: boolean
      }
    },
  ]
  dragMousePosition: [
    {
      id: string
      position: {
        x: number | undefined
        y: number | undefined
        anchor?: string
      }
    },
  ]
  beforeSlideChange: [{ slideId: string }]
  simpleModerationContributionUnder: [{ slideId: string; userId: string }]
  simpleModerationContributionOver: [{ slideId: string; userId: string }]
}

type MediaPermissionsFailureReason =
  | 'camera'
  | 'mic'
  | 'devices'
  | 'screenshare'
  | 'inuse'
  | 'permissions'

export type { DetailedPermissionState } from './meeting/AvMediaManagementCubit'

// TODO: move to cubits as MeetingCubit
/**
 * The Meeting class is the main class that represents a meeting in the application.
 *
 * The Meeting class is responsible for managing the state of the meeting.
 *
 * It houses all observable data and actions that can be performed on the meeting.
 *
 * It also is the access point to any computed properties that are derived from the observable data.
 */
export class MeetingCubit extends Cubit {
  firestore: Firestore
  repository: FirebaseRepository
  currentUser: BreakoutUser

  roomId: string
  livekitRoom: Room | null = null
  livekitRoomReady: boolean = false

  lastActivityTS: number = Date.now()

  // needed here because certain actions switch the state of the tab view
  sidebarTab: MeetingSidebarTab = 'peers'
  isSidebarOpen: boolean = true
  lastUserSidebarOpenState: boolean | undefined = undefined
  onMobile: boolean = false

  broadcastState: BroadcastState = BroadcastState.uninitialized
  broadcastPosition: number | null = null
  broadcastDuration: number | null = null
  activeSpeakerId: string | null = null
  progressBarValue: number = 0
  isPreview: boolean = false
  useMockResults: boolean = false
  displayCaptions: boolean = false
  displayTranscript: boolean = false
  avCheckPassed: boolean = false
  framesReceived: boolean = false
  slideIsClosing: boolean = false

  isCurrentUserSpeaking: boolean = false

  // determines whether we show the talk track component
  isModeratorSpeaking: boolean = false

  // used to determine if we should enable scroll on the meeting view
  screenShareActive = false

  unsubscribers: (() => void)[] = []

  // userAnswers: IObservableArray<UserAnswer> = observable.array([])
  // groupAnswers: IObservableArray<UserAnswer> = observable.array([])
  // questionAnswers: ObservableMap<string, UserAnswer[]> = observable.map({})
  // userIds: IObservableArray<string> = observable.array([])
  // groupLeaderUserIds: IObservableArray<string> = observable.array([])
  // users: ObservableMap<string, string> = observable.map({})
  // userList: IObservableArray<PublicUser> = observable.array([])

  exhibits: ObservableCollection<FirestoreSlideDeckExhibit> | null = null

  streamStatus: ObservableMap<
    string,
    ObservableMap<
      string,
      {
        status: LivekitStreamStatus
        position: number
        duration: number
      }
    >
  > = observable.map({})

  _userIdsAcknowledgedAsAbsent = observable.set<string>([])

  /**
   * indicated whether the user has confirmed that they will be proceeding
   * through the experience alone, if true no need to continue warning the user
   */
  _userAcknowledgedSoloExperience = false

  /**
   * if user acknowledged group experience warning less than 5 minutes ago,
   * no need to show stage 1 dialog (group or solo)
   */
  _userAcknowledgedGroupExperienceAt: Date | null = null

  localCommands: EventEmitter<MeetingEvents>

  // These are supporting classes that are used to break up the Meeting class
  // They should never be used directly outside the Meeting class, so they are private
  protected dataFetcher: MeetingDataFetcher
  protected livekitController: MeetingLivekitController
  protected slideTimerController: SlideTimerController
  moderationTasksController: ModerationTasksController
  devModeController: DevModeController
  avMediaManagement: AvMediaManagementCubit

  _deviceConfigurationController?: {
    connectedParticipantIds: string[]
    inMeetingParticipantIds: string[]
    connectionStatus: 'waiting' | 'ready' | 'regenerating' | 'error'
    disconnect: () => void
  }

  assignment: SectionAssignment
  roomState: RoomState
  slideDeck: SlideDeck
  roles: StaticModelCollection<SlideDeckRole>
  roomToken: UserProfileRoomToken
  section: Section

  _slideDeckExhibits: StaticModelCollection<SlideDeckExhibit>
  slideDeckSlides: StaticModelCollection<SlideModel>
  slideDeckQuestions: StaticModelCollection<SlideQuestion>
  peerReviewQuestions: StaticModelCollection<SectionAssignmentPeerReviewQuestion>
  roomStateAnswersForGroup: StaticModelCollection<RoomStateAnswer>
  roomStateFeedback: RoomStateFeedback
  roomStateSummary: RoomStateSummary
  settingsProcessingVideo: SettingsProcessingVideo
  defaultProcessingAsset: SettingsDefaultProcessingAsset
  defaultProcessingMedia: Media
  slideCaptions: StaticModelCollection<SlideCaption>
  collaborationData: StaticModelCollection<RoomStateCollaborationData>
  sectionPassPricing: SettingsSectionPassPricing
  preMeetingClickWrap: ClickWrap
  preMeetingTermsAndConditions: SettingsTermsAndConditions

  /** Warning: This is not a fully-fledged ValidLibraryObject, it does not have a peerReviewController. */
  libraryObject: ValidLibraryObject

  devModeSlideId: string | undefined

  roomFailedToLoad: boolean = false

  roomStateAnswersPerUser = observable.map<
    string,
    StaticModelCollection<RoomStateAnswer>
  >()

  participantIds = observable.array<string>([])
  secretaryController: SecretaryConnectionController

  publishedParticipantIds = observable.array<string>([])

  seenExhibitIds: ObservableSet<string> = observable.set<string>([])

  mediaPermissionsFailed: MediaPermissionsFailureReason | undefined = undefined

  // This controls the "Show results" button visibility
  endOfSessionQuizSlideCanProceed: boolean = false
  // This actually controls the logic - if it's true, we skip processing
  endOfSessionQuizSlideShouldProceed: boolean = false

  speakingWhileMuted: boolean = false
  ignoreMuteWarning: boolean = false
  streamPlaying: boolean = false
  useStreamsInFetcher: boolean = false

  // This flag is used for QA tooling to stop processing of the transcript
  qa_transcriptProcessingDisabled: boolean = false

  // This stores when the user connected to the room
  userConnectionTimes = observable.map<string, number>()

  showNudgeButton: boolean = false
  waitingForModeratorResponse: boolean = false

  /**
   * use in place of [roomState.roomStartedAt] if
   * if it is no set for some reason
   */
  protected _cubitInitializedAtMs: number

  slideChangeHook: ((index: number) => Promise<void>) | null = null

  simpleModerationCubit: MeetingSimpleModerationCubit =
    new MeetingSimpleModerationCubit(this)

  constructor(
    currentUser: BreakoutUser,
    repository: FirebaseRepository,
    roomId: string,
    options: {
      useStreamsInFetcher?: boolean
      devModeSlideId?: string
      slideDeckPreviewData?: SlideDeckPreviewData
    } = {}
  ) {
    super()
    this._cubitInitializedAtMs = Date.now()
    this.roomId = roomId
    this.currentUser = currentUser

    this.useStreamsInFetcher = options.useStreamsInFetcher ?? false

    this.repository = repository
    this.firestore = repository.firestore

    this.assignment = SectionAssignment.empty(repository)
    this.roomState = RoomState.empty(repository)
    this.slideDeck = SlideDeck.empty(repository)
    this.roomToken = UserProfileRoomToken.empty(repository)

    this._slideDeckExhibits = SlideDeckExhibit.emptyCollection(repository)
    this.slideDeckSlides = SlideModel.emptyCollection(repository)
    this.slideDeckQuestions = SlideQuestion.emptyCollection(repository)
    this.roles = SlideDeckRole.emptyCollection(repository)
    this.peerReviewQuestions =
      SectionAssignmentPeerReviewQuestion.emptyCollection(repository)
    this.roomStateAnswersForGroup = RoomStateAnswer.emptyCollection(repository)
    this.roomStateFeedback = RoomStateFeedback.empty(repository)
    this.roomStateSummary = RoomStateSummary.empty(repository)
    this.settingsProcessingVideo = SettingsProcessingVideo.empty(repository)
    this.defaultProcessingAsset =
      SettingsDefaultProcessingAsset.empty(repository)
    this.defaultProcessingMedia = Media.empty(repository)
    this.slideCaptions = SlideCaption.emptyCollection(repository)
    this.section = Section.empty(repository)
    this.collaborationData =
      RoomStateCollaborationData.emptyCollection(repository)
    this.preMeetingClickWrap = ClickWrap.empty(repository)
    this.preMeetingTermsAndConditions =
      SettingsTermsAndConditions.empty(repository)

    this.localCommands = new EventEmitter<MeetingEvents>()
    this.dataFetcher = this.constructDataFetcher(
      options.slideDeckPreviewData !== undefined
    )
    this.livekitController = this.constructLivekitController()
    this.slideTimerController = this.constructSlideTimerController()
    this.moderationTasksController = this.constructModerationTasksController()
    this.devModeController = this.constructDevModeController()
    this.avMediaManagement = new AvMediaManagementCubit(this.logEvent)
    this.secretaryController = new SecretaryConnectionController(this)

    this.devModeSlideId = options.devModeSlideId

    this.sectionPassPricing = SettingsSectionPassPricing.empty(repository)

    this.libraryObject = new ValidLibraryObject({
      repository: this.repository,
      section: this.section,
      assignment: this.assignment,
      roomState: this.roomState,
      slideDeck: this.slideDeck,
      sectionPassPricing: this.sectionPassPricing,
      hasGrading: false,
    })

    makeObservable(this, {
      // observables
      livekitRoom: observable,
      livekitRoomReady: observable,
      broadcastState: observable,
      sidebarTab: observable,
      broadcastPosition: observable,
      broadcastDuration: observable,
      activeSpeakerId: observable,
      progressBarValue: observable,
      displayCaptions: observable,
      displayTranscript: observable,
      screenShareActive: observable,
      mediaPermissionsFailed: observable,
      roomFailedToLoad: observable,
      avCheckPassed: observable,
      framesReceived: observable,
      endOfSessionQuizSlideCanProceed: observable,
      endOfSessionQuizSlideShouldProceed: observable,
      speakingWhileMuted: observable,
      ignoreMuteWarning: observable,
      streamPlaying: observable,
      lastActivityTS: observable,
      isModeratorSpeaking: observable,
      isCurrentUserSpeaking: observable,
      isSidebarOpen: observable,
      onMobile: observable,
      showNudgeButton: observable,
      waitingForModeratorResponse: observable,
      slideIsClosing: observable,
      shouldBlockNextSlideButtonCallbacks: observable,
      _deviceConfigurationController: observable,

      // actions
      setAvCheckPassed: action,
      setSidebarTab: action,
      setSlide: action,
      setDeviceConfigurationController: action,
      clearDeviceConfigurationController: action,
      // replaceUserAnswers: action,
      // replaceGroupAnswers: action,
      // replaceQuestions: action,
      setLivekitRoomReady: action,
      setLivekitRoom: action,
      setBroadcastState: action,
      setBroadcastPosition: action,
      setBroadcastDuration: action,
      clearBroadcastDuration: action,
      setBroadcastPositionAndDuration: action,
      updateSlideStreamStatus: action,
      updateRoomState: action,
      updateActiveSpeaker: action,
      updateProgressBar: action,
      shareExhibit: action,
      stopSharingExhibit: action,
      toggleCaptions: action,
      toggleTranscript: action,
      updateParticipantIds: action,
      updatePublishedParticipantIds: action,
      setMediaPermissionsFailed: action,
      markRoomFailedToLoad: action,
      markExhibitAsSeen: action,
      markUsersAbsent: action,
      toggleBroadcastPlaying: action,
      onFramesReceived: action,
      resetFramesReceived: action,
      setCanProceedOnEndOfSessionSlide: action,
      setShouldProceedOnEndOfSessionSlide: action,
      markIgnoreMuteWarning: action,
      setSpeakingWhileMuted: action,
      setStreamPlaying: action,
      markActivity: action,
      addUserConnectionTime: action,
      setIsModeratorSpeaking: action,
      setIsCurrentUserSpeaking: action,
      setSidebarOpen: action,
      toggleSidebarOpen: action,
      setOnMobile: action,
      setShowNudgeButton: action,
      setWaitingForModeratorResponse: action,
      setSlideIsClosing: action,
      addShouldBlockNextSlideButtonCallback: action,
      removeShouldBlockNextSlideButtonCallback: action,
      removeAllShouldBlockNextSlideButtonCallbacks: action,

      // computeds
      breakoutParticipantIsPresent: computed,
      userAnswers: computed,
      groupAnswers: computed,
      livekitToken: computed,
      questionAnswers: computed,
      livekitUrl: computed,
      activeSlide: computed,
      activeSlideChangedAt: computed,
      currentSlide: computed,
      previousSlide: computed,
      currentUserIsGroupLeader: computed,
      currentSlideExhibits: computed,
      currentSlideQuestions: computed,
      currentSlideSortableQuestions: computed,
      discussion_count: computed,
      discussion_index: computed,
      timeLeftInSession: computed,
      currentSlideIsBroadcast: computed,
      currentSlideDuration: computed,
      minutesLeftOnSlide: computed,
      rolesInvalidAndShouldBlockNextSlide: computed,
      shouldShowSoloExperienceWarningDialog: computed,
      shouldShowAbsentUsersDialog: computed,
      shouldShowStillWatchingDialog: computed,
      usersStillWatching: computed,
      usersNotRespondedToPoll: computed,
      shouldShowNotRespondedDialog: computed,
      // userMap: computed,
      isSharingExhibit: computed,
      isSequestered: computed,
      // groupLeaderUser: computed,
      broadcastIsReady: computed,
      hasFeedback: computed,
      slides: computed,
      users: computed,
      presentUserIds: computed,
      groupLeaderUserIds: computed,
      allExhibitsSorted: computed,
      hiddenUserIds: computed,
      isDemo: computed,
      slideProgressIsComplete: computed,
      unseenExhibitCount: computed,
      canScreenshare: computed,
      videoMethod: computed,
      isSoloExperience: computed,
      userIds: computed,
      answerObjectMap: computed,
      userColors: computed,
      requiresPayment: computed,
      // currentUserColor: computed,
      shouldBlockNextSlideButton: computed,
      dataForQuizIsReady: computed,
      preMeetingAgreementUrl: computed,
      currentUserRole: computed,
      roleNames: computed,
      slideDeckExhibits: computed,
      meetingExhibits: computed,
      exhibitsForDisplay: computed,
      currentExhibitsUpToThisSlide: computed,

      // DEV MODE
      isInSoloMode: computed,
      isInNonSoloMode: computed,
      allUserIds: computed,
    })
  }

  async initialize() {
    this.startIdleChecker()
    this.secretaryController.initialize()
    this.dataFetcher.start()
    this.simpleModerationCubit.initialize()
    this.addEnsureUserPeerReviewRecordReaction()
    if (!this.isPreview) {
      this.chat.initialize()
      this.sequestrationController.initialize()
    }

    if (!this.isPreview) {
      this.addReaction({
        whenThisChanges: () => this.activeExhibit,
        thenRunThisCode: (exhibit) => {
          if (this.currentSlideIsEndOfSessionQuiz) return
          if (exhibit) {
            this.setSidebarTab('peers')
          } else {
            this.setSidebarTab('exhibits')
          }
        },
      })
    }
  }

  private addEnsureUserPeerReviewRecordReaction() {
    this.addReaction({
      // React when peer review questions finish loading
      whenThisChanges: () => ({
        questionsLoaded: this.peerReviewQuestions.isLoaded,
        hasQuestions: this.peerReviewQuestions.models.length > 0,
        sectionId: this.roomState.data.sectionId,
        assignmentId: this.roomState.data.assignmentId,
        userIds: this.roomState.data.userIds,
      }),
      thenRunThisCode: async ({
        questionsLoaded,
        hasQuestions,
        sectionId,
        assignmentId,
        userIds,
      }) => {
        if (!questionsLoaded) return
        if (!hasQuestions) return
        if (!sectionId || !assignmentId) return
        if (!userIds.includes(this.currentUser.uid)) return

        try {
          await ensureUserPeerReviewRecord(this.repository, {
            roomId: this.roomId,
            sectionId,
            assignmentId,
            userId: this.currentUser.uid,
          })
        } catch (error) {
          console.error(
            '[MeetingCubit] Failed to ensure user peer review record',
            error
          )
          this.logEvent('meeting_ensure_user_peer_review_record_failed')
        }
      },
    })
  }

  onRoomReady() {
    if (this.isDevMode) {
      this.devModeController.initialize()
    }
  }

  startLiveKitController() {
    if (!this.isPreview) {
      this.livekitController.initialize()
      // moderation tasks controller depends on moderator attributes
      this.moderationTasksController.initialize()
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  constructDataFetcher(hasSlideDeckPreviewData?: boolean): MeetingDataFetcher {
    return new MeetingDataFetcher(this, {
      useStreams: this.useStreamsInFetcher,
    })
  }

  constructSlideTimerController(): SlideTimerController {
    return new SlideTimerController(this)
  }

  constructModerationTasksController() {
    return new ModerationTasksController(this)
  }

  constructLivekitController(): MeetingLivekitController {
    return new MeetingLivekitController(this)
  }

  constructDevModeController(): DevModeController {
    return new DevModeController(this)
  }

  onFramesReceived() {
    if (!this.framesReceived) {
      this.logEvent('meeting_frames_received')
    }

    this.framesReceived = true
  }

  resetFramesReceived() {
    this.framesReceived = false
  }

  setSlideIsClosing(value: boolean) {
    this.slideIsClosing = value
  }

  setCanProceedOnEndOfSessionSlide(value: boolean) {
    this.endOfSessionQuizSlideCanProceed = value
  }

  setShouldProceedOnEndOfSessionSlide(value: boolean) {
    this.endOfSessionQuizSlideShouldProceed = value
  }

  setSpeakingWhileMuted(speaking: boolean) {
    if (speaking && !this.speakingWhileMuted) {
      this.logEvent('meeting_speaking_while_muted')
    }
    this.speakingWhileMuted = speaking
  }

  setStreamPlaying(playing: boolean) {
    if (!this.currentSlide?.isVideoSlide && playing === true) {
      // if we're not on a video slide, it's not possible for it to be true - it might be
      // a race condition - we override it to false
      playing = false
    }

    this.streamPlaying = playing
    const isMuted = this.livekitController.isMuted
    if (playing) {
      if (!isMuted) this.mute(true)
    } else if (this.lastMuteWasAutomatic) {
      this.automaticUnmute()
    }
  }

  markIgnoreMuteWarning() {
    this.ignoreMuteWarning = true
  }

  addSlideChangeHook(hook: () => Promise<void>) {
    this.debug('installing slide change hook')
    this.slideChangeHook = hook
  }

  private _sequestrationController?: SequestrationController
  get sequestrationController() {
    return (this._sequestrationController ??= new SequestrationController(
      this,
      this.livekitController
    ))
  }

  get userAcknowledgedSoloExperience() {
    return this._userAcknowledgedSoloExperience
  }

  get userAcknowledgedGroupExperienceAt() {
    return this._userAcknowledgedGroupExperienceAt
  }

  get transcriptController() {
    return this.livekitController.transcriptController
  }

  get rolesInvalidAndShouldBlockNextSlide() {
    const beforeProcessingOrEndOfSessionSlide = (() => {
      // get all slides after this slide
      const currentIndex = this.slideDeckSlides.models.findIndex(
        (s) => s.id === this.currentSlide?.id
      )
      const slidesInFuture = this.slideDeckSlides.models.slice(currentIndex + 1)
      return slidesInFuture.some(
        (slide) =>
          slide.data.slideType === SlideType.endOfSession ||
          slide.data.slideType === SlideType.processingData
      )
    })()

    // we don't care about roles if the grading is already done
    if (!beforeProcessingOrEndOfSessionSlide) return false

    const roleRequirementsMet = doUsersSatisfyRoleRequirements({
      users: [...this.participantIds, this.repository.uid],
      roles: this.roles.models,
      roleAssignment: this.roomState.data.assignmentRoles,
    })

    return !roleRequirementsMet
  }

  shouldBlockNextSlideButtonCallbacks = observable.map<
    string,
    undefined | (() => NextSlideButtonBlockReason | false)
  >()

  get shouldBlockNextSlideButton() {
    if (this.isPreview) return false

    const currentSlideId = this.currentSlide?.id
    if (!currentSlideId) return false

    if (this.rolesInvalidAndShouldBlockNextSlide)
      return 'invalid_role_assignment'

    const callback =
      this.shouldBlockNextSlideButtonCallbacks.get(currentSlideId)

    if (!callback) return false
    return callback()
  }

  addShouldBlockNextSlideButtonCallback(
    slideId: string,
    callback: () => NextSlideButtonBlockReason | false
  ) {
    this.shouldBlockNextSlideButtonCallbacks.set(slideId, callback)
  }

  removeShouldBlockNextSlideButtonCallback(slideId: string) {
    this.shouldBlockNextSlideButtonCallbacks.delete(slideId)
  }

  removeAllShouldBlockNextSlideButtonCallbacks() {
    this.shouldBlockNextSlideButtonCallbacks.clear()
  }

  /// Relations
  chatController?: MeetingChatController
  get chat() {
    this.chatController ??= new MeetingChatController({
      repository: this.repository,
      roomId: this.roomId,
      isDevRoom: this.isDevMode,
      meeting: this,
    })
    return this.chatController
  }

  // This function houses all the logic that should be run when the active slide changes
  onSlideChange() {
    this.changeTabOnSlideChange()

    this.resetFramesReceived()
    this.setBroadcastState(BroadcastState.uninitialized)
    this.setBroadcastPosition(0)
    this.setBroadcastDuration(null)
    this.setStreamPlaying(false)
    // reset the slide change hook
    // hide the dialog
    this.moderationTasksController.setShowDisplay(false)
    this.livekitController.onSlideChange()
    this.livekitController.stopScreenShare()
    if (this.lastMuteWasAutomatic) {
      this.lastMuteWasAutomatic = false
      this.automaticUnmute()
    }

    this.localCommands.emit('slideChanged')
  }

  async onBeforeSlideChange() {
    this.removeAllShouldBlockNextSlideButtonCallbacks()
  }

  changeTabOnSlideChange() {
    if (this.isDevMode) return
    const slideType = this.currentSlide?.type

    // only hide sidebar if the current slide wants it and if the user did not manually open it
    if (this.currentSlideAutoClosesSidebar && !this.lastUserSidebarOpenState) {
      this.setSidebarOpen(false)
    } else if (this.previousSlideAutoClosedSidebar) {
      this.setSidebarOpen(true)
    }

    if (slideType === SlideType.discussion) {
      if (this.currentSlide?.hasImage || this.currentSlide?.hasMarkdown) {
        this.setSidebarTab('peers')
      } else if (this.roomState.data.activeSlide === 0) {
        // if the first slide is a discussion slide, set the sidebar tab to agenda
        this.setSidebarTab('agenda')
      } else {
        this.setSidebarTab('exhibits')
      }
    } else if (slideType === SlideType.professorFeedback) {
      if (this.currentSlide?.hasImage) {
        this.setSidebarTab('peers')
      }
    } else if (slideType === SlideType.video) {
      this.setSidebarTab('peers')
    } else if (slideType === SlideType.endOfSession) {
      this.setSidebarTab('agenda')
    }
  }

  dispose() {
    this.secretaryController.dispose()
    this.stopIdleChecker()
    this.dataFetcher.dispose()
    this.unsubscribers.forEach((sub) => sub())
    this.livekitController.dispose()
    this.sequestrationController.dispose()
    this.slideTimerController.dispose()
    this.moderationTasksController.dispose()
    this.devModeController.dispose()
    this.simpleModerationCubit.dispose()
    this.slideChangeHook = null
    if (!this.isPreview) {
      this.chat.dispose()
    }
    if (this._firstTimeInRoomFunctionTimeout) {
      clearTimeout(this._firstTimeInRoomFunctionTimeout)
    }
    this.avMediaManagement.dispose()
    return super.dispose()
  }

  addUnsubscriber(subscriber: () => void) {
    this.unsubscribers.push(subscriber)
  }

  markRoomFailedToLoad() {
    this.roomFailedToLoad = true
  }

  setBroadcastState(state: BroadcastState) {
    if (state === BroadcastState.stopped) {
      if (this.currentSlide?.type === SlideType.video) {
        this.progressBarValue = 1
      }
    }
    this.broadcastState = state
  }

  setBroadcastPosition(position: number) {
    this.broadcastPosition = position
  }

  setBroadcastDuration(duration: number | null) {
    this.broadcastDuration = duration
  }

  clearBroadcastDuration() {
    this.broadcastDuration = null
  }

  setBroadcastPositionAndDuration(position: number, duration: number) {
    this.broadcastPosition = position
    this.broadcastDuration = duration
    if (this.currentSlide?.type === SlideType.video) {
      this.progressBarValue = position / duration
    }
  }

  setIsModeratorSpeaking(speaking: boolean) {
    if (speaking === this.isModeratorSpeaking) return
    this.isModeratorSpeaking = speaking
    if (speaking) {
      this.setShowNudgeButton(false)
      this.setWaitingForModeratorResponse(false)
    }
  }

  setIsCurrentUserSpeaking(speaking: boolean) {
    if (speaking === this.isCurrentUserSpeaking) return
    this.isCurrentUserSpeaking = speaking
    if (speaking) {
      this.setShowNudgeButton(false)
      this.setWaitingForModeratorResponse(false)
    }
  }

  updateSlideStreamStatus(
    slideId: string,
    userId: string,
    status: LivekitStreamStatus,
    position: number,
    duration: number
  ) {
    const slideStatus = this.streamStatus.get(slideId)

    if (!slideStatus) {
      this.streamStatus.set(slideId, observable.map())
    }

    const slideStatusMap = this.streamStatus.get(slideId)

    slideStatusMap?.set(userId, {
      position: position,
      duration: duration,
      status: status,
    })
  }

  sendStreamStatus(
    status: LivekitStreamStatus,
    position: number,
    duration: number
  ) {
    if (duration > 0) {
      let progress = position / duration
      // we mark stream as complete if we are within 10 seconds of the end
      if (duration - position <= 10) progress = 1
      this.updateProgressBar(progress)
    }
    this.livekitController.sendStreamStatus(status, position, duration)
  }

  // replaceUserAnswers(newUserAnswers: UserAnswer[]) {
  //   this.userAnswers.replace(newUserAnswers)
  // }

  // replaceGroupAnswers(newGroupAnswers: UserAnswer[]) {
  //   this.groupAnswers.replace(newGroupAnswers)
  // }

  // replaceQuestions(newQuestions: MeetingQuestion[]) {
  //   this.questions.replace(newQuestions)
  // }

  updateRoomState = async (newRoomState: RoomState) => {
    if (newRoomState.data === undefined) return

    const hasSlideChanged =
      this.roomState.data.activeSlide !== newRoomState.data.activeSlide
    const wantsToShareExhibit =
      newRoomState.data.activeExhibitId !== undefined &&
      newRoomState.data.activeExhibitId !== this.roomState.data.activeExhibitId

    if (hasSlideChanged) {
      // we are about to change the slide, run the before slide change hook
      await this.onBeforeSlideChange()
    }

    this.roomState.replaceModel(newRoomState)

    if (wantsToShareExhibit) {
      this.moderationTasksController.setShowDisplay(false)
    }

    // slide has changed, reset closing state
    if (hasSlideChanged) {
      this.slideIsClosing = false
    }

    // do not trigger anything if we did not pass the av check
    if ((this.avCheckPassed || this.isDemo) && hasSlideChanged) {
      this.logEvent('loaded_slide', { slide_index: this.activeSlide })
      this.resetSlideTimer()
      this.onSlideChange()
    }
  }

  triggerRoomInitialization() {
    this.debug('triggerRoomInitialization')
    this.firstTimeInRoomFunction()
  }

  // This is the ONLY function that should be called to initialize the room
  // If you ever need to make any changes to the initialization logic,
  // this is the place to do it
  //
  // Now, this does a lot of work that other code does, so this duplicates some logic
  // however it optimizes for a single write in the end
  correctlyInitializeRoom = async () => {
    const activeSlide = this.roomState.data.activeSlide
    const payload: PartialWithFieldValue<FirestoreRoomState> = {}
    if (activeSlide === null || activeSlide === undefined) {
      // Room has not started yet, we need to initialize it
      payload.activeSlide = 0
      payload.activeSlideChangedAt = serverTimestamp()
      payload.roomStartedAt = serverTimestamp()

      // if we are not a group leader, make ourselves a a group leader
      if (
        !this.roomState.data.groupLeaderUserIds.includes(this.repository.uid)
      ) {
        payload.groupLeaderUserIds = [this.repository.uid]
      }

      if (this.devModeController.devModeSlideIndex !== undefined) {
        // if we are in dev mode, set the active slide to the dev mode slide id
        payload.activeSlide = this.devModeController.devModeSlideIndex
      }

      // we are in a room that is not started, start it and become the leader
    } else {
      // Room has activeSlide, so has started

      if (this.isDevMode) {
        const slideIndex = this.devModeController.devModeSlideIndex
        if (slideIndex !== undefined && activeSlide !== slideIndex) {
          // if dev mode wants different slide, switch to it
          payload.activeSlide = slideIndex
        } else {
          // if we are already on the dev mode slide, just pump the slide
          payload.pumpSlide = increment(1)
          payload.updatedAt = serverTimestamp()
        }
      }
    }

    const newPayload = this.runNonDemoFirstTimeInRoomFunction(payload)

    // eslint-disable-next-line no-console
    console.log(
      'meeting_cubit] correctlyInitializeRoom',
      JSON.stringify({
        activeSlide,
        devModeSlide: this.devModeSlideId,
        newPayload,
      })
    )

    // upon initialization, clear the dev mode slide id so that reset
    // will take use to slide 0
    this.devModeSlideId = undefined

    // If nothing changed, do not update the room state
    if (Object.keys(newPayload).length === 0) return

    await updateRoomState(
      this.repository.firestore,
      this.roomState.id,
      newPayload
    )
  }

  _firstTimeInRoomFunctionTimeout: NodeJS.Timeout | null = null
  firstTimeInRoomFunction = async () => {
    // Do not run the first time in room function if
    // the livekit room is not ready - we depend on its state, so when
    // it's not ready, we can't correctly decide what to do
    //
    // we also depend on a few pieces of data that might be loading
    // so we wait until they are ready
    if (
      !this.livekitRoomReady ||
      this.slideDeckSlides.isLoading ||
      this.roomState.isLoading
    ) {
      this._firstTimeInRoomFunctionTimeout = setTimeout(() => {
        this.firstTimeInRoomFunction()
      }, 250)
      return
    }

    this.onSlideChange()

    await this.correctlyInitializeRoom()

    this.resetSlideTimer()

    if (
      this.slides.length &&
      (this.currentSlide?.type === SlideType.video ||
        this.currentSlide?.type === SlideType.processingData) &&
      this.activeSlide !== null
    ) {
      this.dataFetcher.resetCaptions()

      // only run the following in broadcast mode
      if (!this.streamingMode) {
        // if there is no breakout participant, set the progress bar to 1
        if (
          !this.livekitController.broadcastMonitor?.getBreakoutParticipant()
        ) {
          this.debug('no breakout participant, setting progress bar to 1')
          runInAction(() => {
            this.progressBarValue = 1
            this.broadcastState = BroadcastState.stopped
          })
        } else {
          if (this.currentUserIsGroupLeader) {
            this.debug('group leader, force starting broadcast after room load')
            this.sendBroadcastMessage('play')
          }
        }
      }
    }
  }

  runNonDemoFirstTimeInRoomFunction(
    payload: PartialWithFieldValue<FirestoreRoomState>
  ) {
    // obviously, don't run this in demo mode
    if (this.isDemo) return payload

    const newPayload: PartialWithFieldValue<FirestoreRoomState> = { ...payload }

    // if you are the first person to join room an EMPTY room, AND in userIds, automatically assign
    // yourself group leader and update activeSlideChangedAt field to NOW if its value is older than 61 seconds
    if (
      this.livekitRoomReady &&
      this.roomState.data.userIds.includes(this.currentUser.uid)
    ) {
      this.debug(
        'runNonDemoFirstTimeInRoomFunction: room is ready and user is in userIds'
      )

      // Part 1: become the group leader if nobody else is in the room

      // Get remote participants (I'm local, so I'm not included in this list)
      const participants = Array.from(
        this.livekitRoom?.remoteParticipants.values() || []
      )
      // Ignore breakout and secretary participants - we don't consider them real participants
      const participantsWithoutBreakout = participants.filter(
        (p) =>
          p.identity !== ServerIdentities.BreakoutLearning &&
          p.identity !== ServerIdentities.Secretary &&
          p.identity !== ServerIdentities.Moderator
      )
      const roomUserCount = participantsWithoutBreakout.length

      this.debug(
        `runNonDemoFirstTimeInRoomFunction: roomUserCount ${roomUserCount} isGroupLeader ${this.currentUserIsGroupLeader}`
      )

      // To not have a race condition when currentUserIsGroupLeader change
      // we will have an internal variable, that will control part 2
      let canTouchActiveSlideChangedAt = this.currentUserIsGroupLeader

      if (roomUserCount === 0 && !this.currentUserIsGroupLeader) {
        this.debug('no users in room, making self group leader')
        // make yourself group leader, if not already
        newPayload.groupLeaderUserIds = [this.repository.uid]
        // unblock part 2
        canTouchActiveSlideChangedAt = true
      }

      // Part 2: update activeSlideChangedAt field to NOW if its value is older than 61 seconds
      // However, only do this if you are the group leader, otherwise we will get a permission error
      if (canTouchActiveSlideChangedAt) {
        const activeSlideChangedAtTS =
          this.roomState.data.activeSlideChangedAt?.getTime()

        const activeSlideChangedAtTSIsOld =
          activeSlideChangedAtTS && activeSlideChangedAtTS < Date.now() - 61000

        this.debug(
          `runNonDemoFirstTimeInRoomFunction: activeSlideChangedAtTSIsOld ${activeSlideChangedAtTSIsOld}`
        )

        if (activeSlideChangedAtTSIsOld) {
          this.debug('activeSlideChangedAt is old, updating to current time')
          newPayload.activeSlideChangedAt = serverTimestamp()
        }
      }
    }

    return newPayload
  }

  // perform the action associated with the slide change again
  pumpSlide() {
    if (!this.currentUserIsGroupLeader) return
    this.logEvent('pump_slide', { slide_index: this.activeSlide })
    roomStateUpdatePumpSlide(this.firestore, { roomId: this.roomId })
  }

  triggerTokenGeneration() {
    this.logEvent('meeting_token_generation')

    // if token regeneration was requested within the last 15 seconds, do nothing
    // this is to prevent multiple people from requesting tokens at the same time
    const throttleWindow = Date.now() - 15_000
    const requested =
      this.roomState.data.tokenRegenerationRequestedAt?.getTime()

    if (requested && requested > throttleWindow) {
      this.debug('token regeneration throttled')
      return false
    }

    updateRoomState(this.firestore, this.roomId, {
      tokenRegenerationRequestedAt: serverTimestamp(),
    })
    return true
  }

  setSlide(index: number) {
    const newSlideIsDifferent = this.activeSlide !== index

    if (!newSlideIsDifferent) return

    // only the group leader gets the pause on switching to next slide
    if (
      (this.currentUserIsGroupLeader || this.currentUser.isAdmin) &&
      this.slideChangeHook
    ) {
      // if we have a slide change hook, we run it
      this.debug('running slide change hook')
      this.slideChangeHook(index).then(() => {
        this.debug('slide change hook finished')
      })
    }

    // cleanup the hook after running so it only runs once
    this.slideChangeHook = null

    if (this.currentSlideIsBroadcast) {
      this.sendBroadcastMessage('stop')
      this.setBroadcastPosition(0)
    }

    this.resetSlideTimer()
    this.moderationTasksController.setShowDisplay(false)

    const payload: Partial<FirestoreRoomStateWrite> = {
      activeSlide: index,
      activeExhibitId: null,
      activeSlideChangedAt: serverTimestamp(),
    }

    // if admin navigates to the first slide, start the room
    if (index === 0) {
      payload.roomStartedAt = serverTimestamp()
    }

    updateDocWithError(this.roomStateRef(), payload, 'SetSlideError')
  }

  nextSlide() {
    this.logEvent('next_slide', { slide_index: this.activeSlide + 1 })
    if (this.activeSlide < this.slides.length - 1) {
      this.setSlide(this.activeSlide + 1)
    }
  }

  roomStateRef() {
    return doc(collection(this.firestore, 'room_state'), this.roomId)
  }

  setDeviceConfigurationController(
    controller: NonNullable<typeof this._deviceConfigurationController>
  ) {
    this._deviceConfigurationController = controller
  }

  clearDeviceConfigurationController() {
    this._deviceConfigurationController = undefined
  }

  setLivekitRoomReady(ready: boolean) {
    this.livekitRoomReady = ready
    if (ready) this.startLiveKitController()
  }

  setLivekitRoom(room: Room) {
    this.livekitRoom = room
    this.livekitController.setupLivekitListeners()
  }

  sendSoloBotResponseRequest() {
    this.livekitController.sendSoloBotResponseRequest()
  }

  /**
   * Opens a data channel request to the moderator to forward a text message to the Solo Bot.
   * Optionally request an immediate spoken response from the bot.
   */
  sendMessageToSoloBotRequest(
    message: string,
    options?: {
      requestSoloBotResponse?: boolean
      requestSoloBotInterrupt?: boolean
      sendToSoloBot?: boolean
      agentId?: string
      formElementId?: string
      messageRole?: 'user' | 'assistant'
    }
  ): string {
    const requestId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

    // eslint-disable-next-line no-console
    console.log('[sendMessageToSoloBotRequest]', {
      message,
      ...options,
      requestId,
    })
    this.livekitController.sendMessageToSoloBotRequest(message, {
      ...options,
      requestId,
    })
    return requestId
  }

  // create a temporary method that calls sendMessageToSoloBotRequest
  // but fills it with default values for testing
  sendTestMessageToSoloBotRequest(): string {
    //
    let message = 'We are building an experience for learning.\n'
    message +=
      'aim: Students will develop the ability to critically analyze and interpret the claims within patent documents, identifying the scope, novelty, and technical details of those claims.\n'
    message += 'module_type: conceptual discussion\n'
    message += 'title: Critical Analysis of Patent Claims\n'
    message +=
      'subtitle: Understanding the Nuances of Scope, Novelty, and Technical Details\n'
    message +=
      "key_concepts: ['Patent claim structure', 'Legal language interpretation', 'Scope and limitations of claims', 'Novelty aspects in patents']\n"
    message +=
      "learning_objectives: ['Analyze the structure and components of patent claims.', 'Interpret the legal language used in patent documents.', 'Critically assess the scope and limitations of specific patent claims.', 'Evaluate the novelty aspects of patent claims within the context of existing technology.']\n"

    return this.sendMessageToSoloBotRequest(message, {
      requestSoloBotResponse: true,
      agentId: 'aDCXtBwElhQU5YhgxKpI',
      formElementId: 'rpV8BVAconvxwRU2BUIg',
      sendToSoloBot: false,
    })
  }

  // need to ad another test that sends a another preformatted message
  // that is a follow up question
  sendTestFollowupMessageToSoloBotRequest(): string {
    console.error(new Error().stack)
    const message = `AI Agent Result: TITLE: Patent Claims Analysis
SUBTITLE: Decoding Scope & Novelty

OVERVIEW SUMMARY:
This module immerses participants in the critical examination of patent claims, focusing on understanding their structure, legal language, scope, limitations, and novelty. Using real patent excerpts, participants will dissect the components of claims to identify how they define the invention's boundaries and distinguish it from prior art. Through guided discussions, they will interpret complex legal phrasing, evaluate claim breadth and constraints, and assess novelty claims in the context of existing technologies. Participants will engage in structured activities that promote diverse viewpoints and deepen their ability to critically analyze patent documents, preparing them to navigate patent landscapes effectively.

---

**THE BREAKOUT EXPERIENCE**

---

**SEGMENT 1: Patent Claim Structure & Components**
LO 1: Analyze the structure and components of patent claims.

**Slide 1:**
What are the key structural components of a patent claim, and how do they function together to define an invention?
Take a few moments to individually identify and list the main parts of the patent claims provided (e.g., preamble, transition phrase, body). Then, share your observations with your group, explaining how each part contributes to the claim’s overall definition.

**Slide 2:**
**How would you prioritize the importance of different claim components when analyzing a patent's scope?**
Use the Priority Ranking & Justification activity to rank the following claim components from most to least critical in defining the invention’s boundaries:
- Preamble (context of the invention)
- Transition phrase (e.g., “comprising,” “consisting of”)
- Body (elements and steps)
- Dependent claims (limitations and specifics)
Discuss your rankings and justify why you believe certain components carry more weight in claim interpretation.

| Rank | Claim Component         | Justification Summary                  |
|-------|------------------------|--------------------------------------|
| 1     |                        |                                      |
| 2     |                        |                                      |
| 3     |                        |                                      |
| 4     |                        |                                      |

**Slide 3:**
Based on your analysis, which claim component most significantly impacts how broadly or narrowly a patent protects an invention? Provide two specific reasons for your choice.

---

**SEGMENT 2: Interpreting Legal Language in Patent Claims**
LO 2: Interpret the legal language used in patent documents.

**Slide 1:**
How does the legal language in patent claims influence the interpretation of an invention’s scope?
Individually, read the selected claim excerpts and note any ambiguous or complex legal terms. Share with your group how these terms might affect the understanding of the claim’s coverage.

**Slide 2:**
**Using a SWOT analysis, what are the strengths and weaknesses of the legal language in the provided patent claims?**
Analyze the legal phrasing focusing on:
- Strengths: clarity, precision, enforceability
- Weaknesses: ambiguity, potential loopholes, overly broad or narrow terms
- Opportunities: ways to improve claim language for better protection
- Threats: risks of misinterpretation or invalidation
Use the SWOT framework below to organize your discussion.

| SWOT Quadrant | Analysis Points (Examples)                     |
|---------------|-----------------------------------------------|
| Strengths     |                                               |
| Weaknesses    |                                               |
| Opportunities |                                               |
| Threats       |                                               |

**Slide 3:**
Considering your SWOT analysis, what recommendations would you make to improve the legal language of these claims to better protect the invention? Cite specific examples from the claims.

---

**SEGMENT 3: Scope, Limitations & Novelty Assessment**
LO 3: Critically assess the scope and limitations of specific patent claims.
LO 4: Evaluate the novelty aspects of patent claims within the context of existing technology.

**Slide 1:**
What factors determine the scope and limitations of a patent claim, and how do these affect the claim’s novelty?
Take a few moments to individually identify phrases or elements in the claims that set boundaries or limitations. Then discuss how these might influence the claim’s novelty relative to known technology.

**Slide 2:**
**Brainstorm and Filter: What elements in the claims contribute to novelty, and which might limit it?**
Generate a list of claim features that you believe establish novelty (e.g., unique technical details, new combinations) and those that might impose limitations (e.g., specific materials, configurations). Then, categorize these features and select the top three that most strongly impact novelty.

| Category  | Claim Features                      | Impact on Novelty (Positive/Negative) |
|-----------|-----------------------------------|--------------------------------------|
| Novelty   |                                   |                                      |
| Limitation|                                   |                                      |

**Slide 3:**
Based on your group’s findings, what is your overall assessment of the claim’s novelty and scope? Would you consider the claim broad or narrow? Support your position with two examples from the claim text.

---

This design uses a variety of discussion tasks—Priority Ranking, SWOT Analysis, Brainstorm & Filter—to align with the learning objectives and stimulate diverse perspectives. Each segment builds from understanding claim structure to interpreting legal language, then to evaluating scope and novelty, ensuring a logical progression and rich engagement. Exhibits can include selected patent claim excerpts similar to those in the provided patent document for reference during discussions .
`

    return this.sendMessageToSoloBotRequest(message, {
      requestSoloBotResponse: true,
      agentId: '1GHQzazdonLOAe5giGJT',
      formElementId: 'rpV8BVAconvxwRU2BUIg',
      sendToSoloBot: false,
    })
  }

  sendBroadcastMessage(message: string) {
    // if streaming, no need to send broadcast message
    if (this.streamingMode) return
    // Only group leaders can send broadcast messages
    if (!this.currentUserIsGroupLeader) return
    // Do not send stop when the broadcast is already stopped
    if (this.broadcastState === BroadcastState.stopped) return

    if (message === 'stop') {
      this.logEvent('room_video_stop')
    }
    this.logEvent('room_video_action', { action: message })

    this.livekitController.sendBroadcastMessage(message)
  }

  muteParticipant(userId: string) {
    // authorization set vis feature flag
    if (!this.currentUserAllowedToMute) return

    this.logEvent('meeting_mute_participant', {
      user_id: this.repository.uid,
      muted_user_id: userId,
    })

    this.livekitController.sendParticipantMessage(userId, 'muteMic')
  }

  pauseStreamForEveryone() {
    this.livekitController.sendStreamPauseMessage()
  }

  sendDragPosition(data: {
    id: string
    position: {
      x: number | undefined
      y: number | undefined
      anchor?: string
    }
    isDragged: boolean
    label?: string
    userId?: string
    placeholder?: boolean
  }) {
    if (this.isPreview) return
    this.livekitController.sendDragPosition(data)
  }

  sendDragMousePosition(
    id: string,
    position: { x: number | undefined; y: number | undefined; anchor?: string }
  ) {
    if (this.isPreview) return
    // console.log('sendDragMousePosition', id, position)
    this.livekitController.sendDragMousePosition(id, position)
  }

  sendDragListPosition(
    id: string,
    position: {
      idBefore: string | undefined
      containerId: string | undefined
      placeholder?: boolean
    }
  ) {
    if (this.isPreview) return
    this.livekitController.sendDragListPosition(id, position)
  }

  sendBeforeSlideChange(slideId: string) {
    if (this.isPreview) return
    this.livekitController.sendBeforeSlideChange(slideId)
  }

  toggleBroadcastPlaying = () => {
    if (!this.currentUserIsGroupLeader) return
    if (this.broadcastState === BroadcastState.stopped) {
      this.broadcastPosition = 0
      this.pumpSlide()
      return
    }

    this.logEvent('meeting_broadcast_toggle')

    const isPlaying = this.broadcastState === BroadcastState.playing
    this.sendBroadcastMessage(isPlaying ? 'pause' : 'play')
  }

  setSidebarTab(tab: MeetingSidebarTab) {
    this.logEvent('meeting_sidebar_tab_change', { tab })
    this.sidebarTab = tab
  }

  setSidebarOpen(open: boolean) {
    this.isSidebarOpen = open
  }

  toggleSidebarOpen() {
    const newState = !this.isSidebarOpen
    this.setSidebarOpen(newState)
    if (this.currentSlideAutoClosesSidebar) {
      this.lastUserSidebarOpenState = newState
    }
  }

  setOnMobile(onMobile: boolean) {
    this.onMobile = onMobile
  }

  cancelSlideTimer() {
    this.slideTimerController.cancelSlideTimer()
  }

  resetSlideTimer() {
    this.debug('resetting slide timer')
    this.slideTimerController.resetSlideTimer()
  }

  endTimer() {
    this.slideTimerController.endTimer()
  }

  updateProgressBar(progress: number) {
    if (progress > 1) progress = 1

    this.progressBarValue = progress
  }

  lastMuteWasAutomatic = false
  // Delegates
  mute(automatic = false) {
    const isMuted = this.livekitController.isMuted

    // special rules if running mute when muted
    // as it is possible for the automatic to run mute() when we're already muted
    if (isMuted) {
      if (!this.lastMuteWasAutomatic && automatic) return
      // should there be a race condition, non-automatic mutes should override automatic mutes
      if (this.lastMuteWasAutomatic && automatic === false) {
        this.lastMuteWasAutomatic = false
      }
      return
    }

    if (isMuted) return
    this.lastMuteWasAutomatic = automatic
    this.logEvent('meeting_mute')
    this.livekitController.mute()
  }

  async toggleAudio() {
    if (this.isSequestered) return
    this.logEvent('meeting_toggle_audio', { enabled: this.isAudioEnabled() })

    try {
      const isMuted = this.livekitController.isMuted
      return isMuted ? this.unmute() : this.mute()
    } catch (e) {
      const error = e as Error
      if (error.name === 'NotAllowedError') {
        this.setMediaPermissionsFailed('mic')
      }
    }
  }

  automaticUnmute() {
    // never automatically unmute hidden users
    if (this.currentUserIsHidden) return

    this.unmute()
  }

  unmute() {
    if (this.isSequestered) return

    this.logEvent('meeting_unmute')

    this.livekitController.unmute()
  }

  isAudioEnabled() {
    return this.livekitController.isAudioEnabled()
  }

  async toggleVideo() {
    if (this.isSequestered) return
    this.logEvent('meeting_video_toggle', { enabled: this.isVideoEnabled() })

    try {
      await this.livekitController.toggleVideo()
    } catch (e) {
      const error = e as Error
      if (error.name === 'NotAllowedError') {
        this.setMediaPermissionsFailed('camera')
      }
    }
  }

  async enableVideo() {
    if (this.isSequestered) return
    this.logEvent('meeting_video_toggle')

    try {
      await this.livekitController.enableVideo()
    } catch (e) {
      const error = e as Error
      if (error.name === 'NotAllowedError') {
        this.setMediaPermissionsFailed('camera')
      }
    }
  }

  async republishTracks() {
    if (!this.livekitRoom) return
    // never allow this to run if sequestered
    if (this.isSequestered) return

    const localParticipant = this.livekitRoom.localParticipant
    const allPublications = localParticipant.getTrackPublications()

    // unpublish all tracks
    await Promise.all(
      allPublications.map((pub) => {
        // it's a local participant, so all tracks are local tracks
        return localParticipant.unpublishTrack(pub.track as LocalTrack)
      })
    )

    // wait 2 secs to give server some breathing room
    await new Promise((r) => setTimeout(r, 2000))

    // publish all tracks again - we use setEnabled, let livekit handle the rest
    this.enableVideo()
    this.unmute()
  }

  setMediaPermissionsFailed(mode: MediaPermissionsFailureReason | undefined) {
    this.mediaPermissionsFailed = mode
  }

  disableVideo() {
    this.logEvent('meeting_video_disable')
    this.livekitController.disableVideo()
  }

  isVideoEnabled() {
    return this.livekitController.isVideoEnabled()
  }

  async toggleScreenShare() {
    if (this.isSequestered) return

    this.logEvent('meeting_screenshare_toggled')
    try {
      await this.livekitController.toggleScreenShare()
      this.logEvent('meeting_screenshare_success')
    } catch (e) {
      const error = e as Error
      if (error.name === 'NotAllowedError') {
        this.setMediaPermissionsFailed('screenshare')
        this.logEvent('meeting_screenshare_failed_permissions')
      } else {
        this.logEvent('meeting_screenshare_failed', {
          error: error.message,
        })
      }
    }
  }

  toggleVideoForParticipant(userId: string) {
    this.livekitController.toggleVideoForParticipant(userId)
  }

  seekBroadcast(position: number) {
    this.livekitController.seekBroadcast(position)
  }

  updateActiveSpeaker(userId: string | null) {
    this.activeSpeakerId = userId
  }

  shareExhibit(exhibitId: string): void {
    this.logEvent('meeting_exhibit_share_start', {
      exhibitId,
    })
    this.moderationTasksController.setShowDisplay(false)
    updateDocWithError(
      this.roomStateRef(),
      {
        activeExhibitId: exhibitId,
      },
      'ShareExhibitError'
    )
  }

  stopSharingExhibit(): void {
    this.logEvent('meeting_exhibit_share_stop', {
      exhibitId: this.activeExhibitId,
    })
    updateDocWithError(
      this.roomStateRef(),
      {
        activeExhibitId: null,
      },
      'StopSharingExhibitError'
    )
  }

  async promoteSelfToLeader() {
    await roomStateSetGroupLeaders(this.firestore, this.roomId, [
      this.currentUser.uid,
    ])
    this.slideTimerController.startVideoDidNotStartTimer()
    this.logEvent('meeting_assert_leadership')
  }

  toggleCaptions() {
    this.logEvent('meeting_toggle_captions', {
      // we log the desired state, not the current state
      enabled: !this.displayCaptions,
    })
    this.displayCaptions = !this.displayCaptions
  }

  toggleTranscript() {
    this.logEvent('meeting_toggle_transcript', {
      // we log the desired state, not the current state
      enabled: !this.displayTranscript,
    })
    this.displayTranscript = !this.displayTranscript
  }

  resolveSlide(model: SlideModel, index: number) {
    const type: SlideType = model.data.slideType
    switch (type) {
      case SlideType.sessionResults:
        return new MeetingResultsSlide(this, model, index)

      case SlideType.interactivePoll:
        return new InteractivePollSlide(this, model, index)

      case SlideType.interactiveQuiz:
        return new InteractiveQuizSlide(this, model, index)

      case SlideType.groupQuiz:
        return new GroupQuizSlide(this, model, index)

      case SlideType.soloQuiz:
        return new SoloQuizSlide(this, model, index)

      case SlideType.endOfSession:
        return new EndOfSessionQuizSlide(this, model, index)

      default:
        return new Slide(this, model, index)
    }
  }

  markGroupExperienceAcknowledged() {
    this._userAcknowledgedGroupExperienceAt = new Date()
  }

  markSoloExperience() {
    this._userAcknowledgedSoloExperience = true
  }

  markUsersAbsent(userIds: string[] | string) {
    const absentList = Array.isArray(userIds) ? userIds : [userIds]
    this._userIdsAcknowledgedAsAbsent.replace([
      ...absentList,
      ...this._userIdsAcknowledgedAsAbsent,
    ])
  }

  haveFiveMinutesPassedSinceRoomStart(): boolean {
    const roomStartedAtMs =
      this.roomState.data.roomStartedAt?.valueOf() || this._cubitInitializedAtMs
    const msSinceRoomStart = Date.now() - roomStartedAtMs
    const fiveMinutesMs = 5 * 60 * 1000
    return msSinceRoomStart > fiveMinutesMs
  }

  markExhibitAsSeen(exhibitId: string) {
    this.seenExhibitIds.add(exhibitId)
  }

  markExhibitAsSeenWithDelay(exhibitId: string) {
    setTimeout(() => {
      this.markExhibitAsSeen(exhibitId)
    }, 1000)
  }

  addSelfAsHiddenUser() {
    roomStateAddHiddenUser(this.firestore, {
      roomId: this.roomId,
      userId: this.repository.uid,
    })
  }

  setAvCheckPassed(passed: boolean) {
    this.avCheckPassed = passed
  }

  get userCanBeInMeeting() {
    // do not boot until we know which users are valid
    if (!this.roomState.isLoaded) return true

    const { hiddenUserIds, userIds, groupLeaderUserIds } = this.roomState.data

    return [...hiddenUserIds, ...userIds, ...groupLeaderUserIds].includes(
      this.repository.uid
    )
  }

  get isSoloExperience() {
    return (
      this.roomState.userIds.length === 1 && this.assignment.maxGroupSize === 1
    )
  }

  get isDevMode() {
    return this.roomState.data.slideDeckId === this.roomId
  }

  get shouldShowSoloExperienceWarningDialog() {
    // if user is only user (of many possible) in room and has not acknowledged
    // they are proceeding alone, show the dialog
    return (
      !this.userAcknowledgedSoloExperience &&
      this.roomState.userIds.length === 1 &&
      this.assignment.maxGroupSize > 1 &&
      this.repository.uid === this.roomState.userIds[0]
    )
  }

  get shouldShowAbsentUsersDialog() {
    return this.absentUsers.length > 0
  }

  get usersStillWatching() {
    const slideId = this.currentSlide?.id
    if (!slideId) return []

    const thisSlideStatus = this.streamStatus.get(slideId)

    if (!thisSlideStatus) return []

    const userIds = new Set<string>()

    const keys = Array.from(thisSlideStatus.keys())

    for (const userId of keys) {
      const status = thisSlideStatus.get(userId)
      if (!status) continue

      const isWatching =
        // check if the user is more than 10 seconds from the end
        status.position < status.duration - 10

      if (isWatching) userIds.add(userId)
    }

    return this.users.filter((user) => userIds.has(user.id))
  }

  get shouldShowStillWatchingDialog() {
    const slideId = this.currentSlide?.id
    if (this.isDemo) return false
    if (!slideId) return false
    if (this.currentSlide.type !== SlideType.video) return false

    const thisSlideStatus = this.streamStatus.get(slideId)

    if (!thisSlideStatus) return false

    return this.usersStillWatching.length > 0
  }

  get usersNotRespondedToPoll(): PublicUser[] {
    if (this.isPreview) return []

    const isValidSlideType =
      this.currentSlide instanceof InteractivePollSlide ||
      this.currentSlide instanceof SoloQuizSlide ||
      this.currentSlide instanceof InteractiveQuizSlide

    if (!isValidSlideType) return []

    const questions = this.currentSlide.questions
    const presentUserIds = [...this.presentUserIds, this.currentUser.uid]

    const usersNotRespondedIds = presentUserIds.filter((userId) => {
      const hasCompletedAllQuestions = questions.every((question) =>
        question.allSubmittedAnswers.find(
          (answer) => answer.data.userId === userId
        )
      )
      return !hasCompletedAllQuestions
    })

    return this.users.filter((user) => usersNotRespondedIds.includes(user.id))
  }

  get shouldShowNotRespondedDialog(): boolean {
    return this.usersNotRespondedToPoll.length > 0
  }

  get unseenExhibitCount() {
    const exhibitIds = this.currentExhibitsUpToThisSlide.map(
      (exhibit) => exhibit.id
    )
    return exhibitIds.filter((id) => !this.seenExhibitIds.has(id)).length
  }

  get isSharingExhibit(): boolean {
    return this.activeExhibitId !== null
  }

  async sendFeedback(
    score: number,
    comment: string,
    reasons: string[],
    flagMode?: string
  ): Promise<void> {
    this.logEvent('meeting_feedback_sent')
    return createRoomStateFeedback(this.firestore, {
      comment: comment,
      reasons: reasons,
      roomId: this.roomId,
      score: score,
      userId: this.currentUser.uid,
      flagMode: flagMode,
    })
  }

  updateParticipantIds(ids: string[]) {
    this.participantIds.replace(ids)
    this.secretaryController.handleParticipantIdsUpdate(ids)

    // if a user is not in participantIds, it means they disconnected
    // which means we want to remove them from stream status
    for (const slideStreamStatus of this.streamStatus.values()) {
      const streamIds = Array.from(slideStreamStatus.keys())
      for (const id of streamIds) {
        if (ids.includes(id)) continue
        slideStreamStatus.delete(id)
      }
    }
  }

  updatePublishedParticipantIds(ids: string[]) {
    this.publishedParticipantIds.replace(ids)
  }

  async reconnectRoom() {
    if (!this.livekitRoom) return

    await this.livekitRoom.disconnect()
    await new Promise((resolve) => setTimeout(resolve, 1000))
    await this.livekitRoom.connect(this.livekitUrl, this.livekitToken, {
      autoSubscribe: true,
    })
  }

  // Computed properties
  get deviceConfigurationController() {
    return this._deviceConfigurationController
  }

  get slideProgressIsComplete() {
    return this.progressBarValue >= 1
  }

  get isDemo() {
    return !!this.roomState.data.isDemo
  }

  // @computed
  get breakoutParticipantIsPresent() {
    return !!this.livekitController.broadcastMonitor?.getBreakoutParticipant()
  }

  // an array of answers for a particular user
  get dataForQuizIsReady() {
    return (
      this.slideDeckQuestions.isLoaded &&
      this.roomStateAnswersPerUser.get(this.currentUser.uid)?.isLoaded
    )
  }

  async quizDataIsReadyForUser(userId: string) {
    return (
      this.slideDeckQuestions.isLoaded &&
      this.roomStateAnswersPerUser.get(userId)?.isLoaded
    )
  }

  get streamingMode() {
    return this.videoMethod === 'streaming'
  }

  /**
   * The priority of room > section > feature flags is observed by the broadcast server
   * if changed here the trigger must also be updated with the new priority
   */
  get videoMethod() {
    const { videoMethod: roomVideoMethod } = this.roomState.data
    const { videoMethod: sectionVideoMethod } = this.section.data

    return (
      roomVideoMethod ??
      sectionVideoMethod ??
      this.repository.featureFlags.data.videoMethod
    )
  }

  get userIds() {
    return this.roomState.data.userIds
  }

  get users() {
    return this.repository.userStore.getUsers(this.roomState.data.userIds)
  }

  get usersLoading() {
    return this.users.some((user) => user.isLoading)
  }

  /**
   * @deprecated use session results from MeetingResultsCubit for future views
   */
  get sessionResults(): {
    pollAgreement: number
    quizPerformance: number
  } {
    /// loop over state.slideQuestions and get questions
    /// if it is a poll type question
    /// loop over state.roomStateAnswersByQuestion and get answers
    /// extract an array of bools from the from questions that are of
    /// type poll. 1 = true, 0 = false
    /// pass the list to the similarity function
    /// add the results from each question to a double and then divide
    /// by the number of questions and set that to pollAgreement
    /// calculate the quizPerformance as well
    /// (simple average of correct answers)
    let pollTotal = 0.0
    let pollCount = 0
    let correctAnswers = 0
    let quizCount = 0

    for (const question of this.questions) {
      if (
        question.questionType === SlideQuestionType.poll ||
        question.questionType === SlideQuestionType.customPoll
      ) {
        const answers = this.questionAnswers.get(question.id) ?? []
        const answerList = answers.map((answer) => answer.data.answer === 1)
        pollTotal += similarity(answerList)
        pollCount++
      } else {
        const answers = this.questionAnswers.get(question.id) ?? []
        for (const answer of answers) {
          if (answer.data.answer === question.data.correct) {
            correctAnswers++
          }
          quizCount++
        }
      }
    }

    const pollAgreement =
      Math.round((pollTotal / Math.max(pollCount, 1)) * 100) / 100
    const quizPerformance =
      Math.round((correctAnswers / Math.max(quizCount, 1)) * 100) / 100

    return {
      pollAgreement,
      quizPerformance,
    }
  }

  // getter for present users where a user is present if they are in users and
  // in the livekit room
  get presentUserIds() {
    const presentIdentities = this.participantIds
    return this.users
      .filter((user) => {
        return presentIdentities.includes(user.id)
      })
      .map((user) => user.id)
  }

  // returns list of users who are not present in the livekit (meeting) room
  // or the acknowledged absent list
  // but ARE present in [roomState.userIds]
  get absentUsers() {
    return this.users.filter(
      (user) =>
        user.id !== this.repository.uid &&
        !this.participantIds.includes(user.id) &&
        !this._userIdsAcknowledgedAsAbsent.has(user.id)
    )
  }

  get userAnswers() {
    const array = this.roomStateAnswersPerUser.get(this.currentUser.uid)

    if (!array) return []

    return array.models
  }

  // @computed
  get groupAnswers() {
    return this.roomStateAnswersForGroup.models
  }

  // @computed
  get activeSlide(): number {
    return this.roomState.data.activeSlide || 0
  }

  // @computed
  get activeSlideChangedAt(): Date | null {
    return this.roomState.data.activeSlideChangedAt || null
  }

  // @computed
  get livekitToken() {
    return this.roomToken.data?.token
  }

  // @computed
  get livekitUrl() {
    return this.roomToken.data?.url
  }

  // @computed
  get activeExhibitId(): string | null {
    return this.roomState.data.activeExhibitId || null
  }

  // @computed
  get slideDeckId() {
    return this.roomState.data.slideDeckId
  }

  // @computed
  get sectionId() {
    return this.roomState.data.sectionId
  }

  // @computed
  get assignmentId() {
    return this.roomState.data.assignmentId
  }

  // @computed
  get slides() {
    const result = this.slideDeckSlides.models.map((model, index) => {
      return this.resolveSlide(model, index)
    })
    return result
  }

  // @computed
  get questions() {
    return this.slideDeckQuestions.models.map((model) => {
      return new MeetingQuestion(this, model)
    })
  }

  // @computed
  get questionAnswers() {
    const questionAnswers = observable.map<string, RoomStateAnswer[]>({})

    for (const answer of this.roomStateAnswersForGroup.models) {
      const questionId = answer.data.slideQuestionId
      const answers = questionAnswers.get(questionId) || []
      answers.push(answer)
      questionAnswers.set(questionId, answers)
    }

    const userIds = this.roomState.userIds
    for (const userId of userIds) {
      const userAnswers = this.roomStateAnswersPerUser.get(userId)

      if (!userAnswers) continue

      for (const answer of userAnswers.models) {
        const questionId = answer.data.slideQuestionId
        const answers = questionAnswers.get(questionId) || []
        answers.push(answer)
        questionAnswers.set(questionId, answers)
      }
    }

    return questionAnswers
  }

  // @computed
  get preMeetingAgreementUrl() {
    return guardOnLoaded(
      [this.preMeetingTermsAndConditions, this.preMeetingClickWrap],
      () => {
        if (this.preMeetingClickWrap.data?.clickWrapUrl) {
          return this.preMeetingClickWrap.data?.clickWrapUrl
        }
        if (this.preMeetingTermsAndConditions.data?.preMeeting) {
          return this.preMeetingTermsAndConditions.data?.preMeeting
        }
        return null
      }
    )
  }

  get currentUserRole() {
    const { assignmentRoles } = this.roomState.data
    if (!assignmentRoles || !(this.currentUser.uid in assignmentRoles))
      return null
    return assignmentRoles[this.currentUser.uid]
  }

  get hasPeerReview() {
    return this.peerReviewQuestions.models.length > 0
  }

  get roleNames(): Record<string, string> {
    if (!this.roles.models.length) return {}

    const assignmentRoles = this.roomState.data.assignmentRoles
    if (!assignmentRoles) return {}

    const result: Record<string, string> = {}
    for (const [identity, roleId] of Object.entries(assignmentRoles)) {
      const role = this.roles.models.find((r) => r.id === roleId)
      if (role?.data.roleName) {
        result[identity] = role.data.roleName
      }
    }
    return result
  }

  get slideDeckExhibits() {
    const { models, isLoading } = this._slideDeckExhibits

    if (isLoading || this.isPreview || this.currentUserIsHidden) {
      return this._slideDeckExhibits
    }

    const modelsForRole = models.filter((exhibit) => {
      const allowedRoles = exhibit.data.assignmentRoles

      // not limiting on role, so let all roles see
      if (!allowedRoles || !allowedRoles.length) return true

      // if we need a role, and the user does not have one, block
      if (!this.currentUserRole) return false

      return allowedRoles.includes(this.currentUserRole)
    })
    if (modelsForRole.length === models.length) return this._slideDeckExhibits
    const filteredForRole = SlideDeckExhibit.emptyCollection(this.repository)
    filteredForRole.replaceModels(modelsForRole)
    return filteredForRole
  }

  private _meetingExhibitCache = new Map<string, MeetingExhibit>()

  get meetingExhibits(): MeetingExhibit[] {
    const models = this.slideDeckExhibits.models
    const newCache = new Map<string, MeetingExhibit>()

    const result = models.map((exhibit) => {
      const cached = this._meetingExhibitCache.get(exhibit.id)
      if (cached && cached.exhibit === exhibit) {
        newCache.set(exhibit.id, cached)
        return cached
      }
      const wrapper = new MeetingExhibit(exhibit, this)
      newCache.set(exhibit.id, wrapper)
      return wrapper
    })

    this._meetingExhibitCache = newCache
    return result
  }

  get currentSlideExhibits(): MeetingExhibit[] {
    const slide = this.currentSlide

    if (!slide) return []

    return this.allExhibitsSorted.filter((exhibit) => {
      return exhibit.data.slideId === slide.id
    })
  }

  get currentExhibitsUpToThisSlide(): MeetingExhibit[] {
    const slide = this.currentSlide
    const slideIdsSeenSoFar = this.slides
      .filter((_slide, index) => {
        return index <= this.activeSlide
      })
      .map((slide) => slide.id)

    if (!slide) return []

    return this.allExhibitsSorted.filter((exhibit) => {
      const slideId = exhibit.data.slideId
      return slideId && slideIdsSeenSoFar.includes(slideId)
    })
  }

  get allExhibitsSorted(): MeetingExhibit[] {
    const exhibitsWithIndices = this.meetingExhibits.map((e) => {
      const index = this.slides.findIndex((s) => s.id === e.data.slideId)
      return { exhibit: e, index }
    })

    return exhibitsWithIndices
      .sort((a, b) => {
        const aIndex = a.index
        const bIndex = b.index
        // sort by index first, then by name
        if (aIndex === bIndex) {
          return a.exhibit.data.exhibitName.localeCompare(
            b.exhibit.data.exhibitName
          )
        }
        return bIndex - aIndex
      })
      .map(({ exhibit }) => exhibit)
  }

  get exhibitsForDisplay(): MeetingExhibit[] {
    const slide = this.currentSlide
    if (!slide) return []

    const displayMode =
      slide.exhibitDisplayMode ?? ExhibitGridDisplayMode.upToNow

    switch (displayMode) {
      case ExhibitGridDisplayMode.all:
        return this.allExhibitsSorted
      case ExhibitGridDisplayMode.currentSlide:
        return this.currentSlideExhibits
      case ExhibitGridDisplayMode.upToNow:
      default:
        return this.currentExhibitsUpToThisSlide
    }
  }

  get activeExhibit(): MeetingExhibit | undefined {
    return this.meetingExhibits.find(
      (exhibit) => exhibit.id === this.activeExhibitId
    )
  }

  get currentUserIsGroupLeader() {
    return this.groupLeaderUserIds.includes(this.currentUser.uid)
  }

  get currentUserAllowedToMute() {
    // switch case on the sessionMuting value
    switch (this.repository.featureFlags.data.sessionMuting) {
      case SessionMuting.all:
        return true
      case SessionMuting.none:
        return false
      case SessionMuting.groupLeader:
        return this.currentUserIsGroupLeader
      default:
        return false
    }
  }

  get groupLeaderUserIds() {
    return this.roomState.data.groupLeaderUserIds
  }

  get hiddenUserIds() {
    return this.roomState.data.hiddenUserIds || []
  }

  isUserHidden(userId: string) {
    return (
      this.hiddenUserIds.includes(userId) &&
      !this.roomState.data.userIds.includes(userId)
    )
  }

  // A user is hidden if they are in the hiddenUserIds list and are not added to userIds
  get currentUserIsHidden() {
    return (
      this.hiddenUserIds.includes(this.currentUser.uid) &&
      !this.roomState.data.userIds.includes(this.currentUser.uid)
    )
  }

  get currentSlideQuestions() {
    // if slide is of type interactivePoll, interactiveQuiz, or groupSorting return the questions object
    if (
      this.currentSlide instanceof InteractivePollSlide ||
      this.currentSlide instanceof InteractiveQuizSlide ||
      this.currentSlide instanceof GroupQuizSlide ||
      this.currentSlide instanceof SoloQuizSlide
    ) {
      return this.currentSlide.questions
    }
    if (
      this.currentSlide?.type === SlideType.sortedSoloInput ||
      this.currentSlide?.type === SlideType.sortedGroupInteractive ||
      this.currentSlide?.type === SlideType.sortedGroupInput
    ) {
      return this.questions.filter((q) => q.slideId === this.currentSlide?.id)
    }

    return []
  }

  get currentSlideSortableQuestions() {
    return this.currentSlideQuestions.filter((q) => {
      return q.questionType === SlideQuestionType.sortableStringList
    })
  }

  get currentSlideTitles() {
    const titlesForSlide = this.questions.filter(
      (q) =>
        q.slideId === this.currentSlide?.id &&
        q.questionType === SlideQuestionType.title
    )
    return sortQuestions({ questions: titlesForSlide, slideIds: [] })
  }

  get previousSlide(): Slide | undefined {
    const previousIndex = this.activeSlide - 1
    if (previousIndex < 0) return undefined
    return this.slides[previousIndex]
  }

  get upcomingSlide(): Slide | null {
    const activeSlide = this.currentSlide
    if (!activeSlide) return this.slides[0] || null

    const activeIndex = activeSlide.index
    return this.slides[activeIndex + 1] || null
  }

  get currentSlide(): Slide | undefined {
    const slide = this.slides[this.activeSlide]
    if (!slide) return this.slides[0]

    return slide
  }

  get currentSlideId() {
    return this.currentSlide?.id
  }

  get currentSlideIsEndOfSessionQuiz() {
    return this.currentSlide?.type === SlideType.endOfSession
  }

  get timeLeftInSession() {
    const slidesLeft = this.slides.filter((_slide, index) => {
      return index > this.activeSlide
    })
    return slidesLeft.reduce((acc, slide) => {
      return acc + slide.durationInMinutes
    }, 0)
  }

  get currentSlideIsBroadcast() {
    return this.currentSlide?.type === SlideType.video
  }

  get currentSlideDuration() {
    if (this.currentSlide?.type === SlideType.video) {
      return this.currentSlide?.slideVideoDuration || 0
    }
    return this.currentSlide?.duration || 0
  }

  get minutesLeftOnSlide() {
    const minutesLeft = Math.round(
      (this.currentSlideDuration / 60) * (1 - this.progressBarValue)
    )

    return minutesLeft
  }

  get progressIsDone() {
    return this.progressBarValue === 1
  }

  get progressIsLoading() {
    return (
      (this.currentSlideDuration === 0 && this.currentSlideIsBroadcast) ||
      !this.progressBarValue
    )
  }

  get discussion_count() {
    return this.slides
      .slice(1)
      .filter((element) => element.type === SlideType.discussion).length
  }

  get discussion_index() {
    return this.slides
      .slice(1, this.activeSlide + 2)
      .filter((element) => element.type === SlideType.discussion).length
  }

  get hasFeedback() {
    return (
      this.roomStateFeedback.hasData && this.roomStateFeedback.data.score >= 0
    )
  }

  get isSequestered() {
    return this.sequestrationController.isSequestered
  }

  get isMuted() {
    return this.livekitController.isMuted
  }

  get groupLeaderUser() {
    const groupLeaderUserIds = this.roomState.data.groupLeaderUserIds
    const id = groupLeaderUserIds[0]
    if (!id) return PublicUser.empty(this.repository)

    return this.repository.userStore.getUser(id)
  }

  get broadcastIsReady() {
    return this.broadcastState !== 'uninitialized'
  }

  get canScreenshare() {
    if (!this.currentSlide) return false

    const { type, hasImage } = this.currentSlide

    return (
      type === SlideType.discussion ||
      type === SlideType.image ||
      type === SlideType.markdown ||
      (type === SlideType.professorFeedback && !hasImage)
    )
  }

  markActivity() {
    this.lastActivityTS = Date.now()
  }

  resetRoomState = async () => {
    await this.removeAllCollaborationData()
    await resetRoomState(this.repository, {
      roomId: this.roomId,
      sectionId: this.sectionId || '',
      assignmentId: this.assignmentId || '',
    })
  }

  removeAllCollaborationData = async () => {
    if (!this.roomId) return

    await deleteAllRoomStateCollaborationData(this.repository, {
      roomId: this.roomId,
    })
  }

  get isInSoloMode() {
    return this.roomState.data.userIds.length === 1
  }

  // Non solo is when we have the same userId twice in the userIds array
  get isInNonSoloMode() {
    return this.roomState.data.userIds.length > 1
  }

  get allUserIds() {
    const userIds = this.roomState.data.userIds
    const hiddenUserIds = this.roomState.data.hiddenUserIds || []
    return Array.from(new Set([...userIds, ...hiddenUserIds]))
  }

  resetPublishedParticipants() {}

  // we use this to log events, and we also piggyback on this to log the last activity
  logEvent = (name: string, params?: Record<string, unknown>) => {
    this.markActivity()
    // do not spam the console in production
    if (!this.repository.inProduction) {
      const message = `[meeting_cubit] ${name}`
      // eslint-disable-next-line no-console
      params ? console.debug(message, params) : console.debug(message)
    }
    this.repository.logEvent(name, {
      room_id: this.roomId,
      assignment_id: this.assignmentId,
      section_id: this.sectionId,
      slide_deck_id: this.slideDeck.id,
      active_slide: this.activeSlide,
      ...params,
    })
  }

  debug(message: string, params?: Record<string, unknown>) {
    this.repository.debug('meeting_cubit', message, {
      params: {
        room_id: this.roomId,
        assignment_id: this.assignmentId,
        section_id: this.sectionId,
        slide_deck_id: this.slideDeck.id,
        ...params,
      },
    })
  }

  idleCheckerInterval: NodeJS.Timeout | null = null
  idleCheckerPeriod = 60_000 // 1 minute
  // 90 minute timeout - this means *nothing* happened in the meeting
  // for 90 minutes - we use events of other people as well
  idleTimeout = 90 * 60_000
  startIdleChecker() {
    this.idleCheckerInterval = setInterval(() => {
      const idleTime = Date.now() - this.lastActivityTS
      if (idleTime > this.idleTimeout) {
        this.logEvent('meeting_idle_timeout')
        this.localCommands.emit('idleTimeout')
      }
    }, this.idleCheckerPeriod)
  }

  stopIdleChecker() {
    if (this.idleCheckerInterval) {
      clearInterval(this.idleCheckerInterval)
      this.idleCheckerInterval = null
    }
  }

  addUserConnectionTime(userId: string, time: number) {
    this.userConnectionTimes.set(userId, time)
  }

  get answerObjectMap(): Record<string, string> {
    const answerObjectMap: Record<string, string> = {}
    for (const answer of this.userAnswers) {
      const answerObjects = answer.data.answerObjects
      if (!answerObjects) continue

      Object.entries(answerObjects).forEach(([id, { text }]) => {
        answerObjectMap[id] = text
      })
    }

    // group answers go second, as they can override user answers
    for (const answer of this.groupAnswers) {
      const answerObjects = answer.data.answerObjects
      if (!answerObjects) continue

      Object.entries(answerObjects).forEach(([id, { text }]) => {
        answerObjectMap[id] = text
      })
    }

    const collaborationDatas = this.collaborationData.models
    if (collaborationDatas) {
      for (const data of collaborationDatas) {
        if (!answerObjectMap[data.id]) {
          answerObjectMap[data.id] = data.data.label || ''
        }
      }
    }

    return answerObjectMap
  }

  private possibleColors: string[] = [
    '#ff6767',
    '#66e0da',
    '#f5a2d9',
    '#f0c722',
    '#6a85e5',
    '#fd9a6f',
    '#92db6e',
    '#73b8e5',
    '#fd7590',
    '#c78ae5',
  ]

  get userColors() {
    const colorMap: Record<string, string> = {}
    const userIds = this.userIds

    userIds.forEach((userId, index) => {
      colorMap[userId] = this.possibleColors[index % this.possibleColors.length]
    })

    return colorMap
  }

  get currentSlideAutoClosesSidebar() {
    const slideType = this.currentSlide?.type

    return (
      slideType === SlideType.sortedGroupInteractive ||
      slideType === SlideType.sortedGroupInput ||
      slideType === SlideType.sortedSoloInput ||
      slideType === SlideType.interactiveQuiz ||
      slideType === SlideType.interactiveForm
    )
  }

  get previousSlideAutoClosedSidebar() {
    const previousSlide = this.previousSlide
    if (!previousSlide) return false
    const slideType = previousSlide.type

    return (
      slideType === SlideType.sortedGroupInteractive ||
      slideType === SlideType.sortedGroupInput ||
      slideType === SlideType.sortedSoloInput ||
      slideType === SlideType.interactiveQuiz ||
      slideType === SlideType.interactiveForm
    )
  }

  // how long the user speech has to be for us to start waiting for a moderator response
  MODERATOR_WAIT_THRESHOLD = 5000

  moderatorNudgeTimeout: NodeJS.Timeout | null = null

  setShowNudgeButton(show: boolean) {
    if (!show && this.moderatorNudgeTimeout) {
      clearTimeout(this.moderatorNudgeTimeout)
      this.moderatorNudgeTimeout = null
    }
    if (show) {
      this.logEvent('meeting_moderator_nudge_button_shown')
    }
    this.showNudgeButton = show
  }

  setWaitingForModeratorResponse(waiting: boolean) {
    // if we're already waiting for moderator response, do not change the state
    if (this.waitingForModeratorResponse === waiting) return

    this.waitingForModeratorResponse = waiting

    if (waiting) {
      const ff = this.repository.featureFlags
      if (!ff.shouldShowNudgeButton) return

      const nudgeTimeoutValue = ff.data.soloBotNudgeButtonTimeout

      // if we started waiting for moderator response and did not get it in the expected time,
      // read from the feature flag, we show the nudge button
      this.moderatorNudgeTimeout = setTimeout(() => {
        this.setShowNudgeButton(true)
        this.setWaitingForModeratorResponse(false)
      }, nudgeTimeoutValue)
    }
  }

  onUserStoppedSpeaking(duration: number) {
    if (this.inSoloBotMode && duration > this.MODERATOR_WAIT_THRESHOLD) {
      this.setWaitingForModeratorResponse(true)
    }
  }

  // not computed because livekit observables don't interact with mobx
  get inSoloBotMode() {
    if (!this.livekitRoom) return false

    const moderatorIfAvailable = Array.from(
      this.livekitRoom.remoteParticipants.values()
    )
      .filter((p) => p.identity === ServerIdentities.Moderator)
      .map((p) => p)

    if (moderatorIfAvailable.length !== 1) {
      return false
    }
    const moderator = moderatorIfAvailable[0]
    const user = this.livekitRoom.localParticipant

    // expect the server to set the listeningTo attribute which tells us
    // who the moderator is currently listening to
    // if it's not set, we assume the moderator is not listening to anyone
    const _attributes = moderator.attributes
    const attributes = _attributes || {}
    const listeningTo = attributes.listeningTo || undefined
    return this.isInSoloMode && listeningTo === user.identity
  }

  get requiresPayment() {
    return (
      this.section.isLoaded &&
      this.assignment.isLoaded &&
      this.roomState.isLoaded &&
      this.slideDeck.isLoaded &&
      this.sectionPassPricing.isLoaded &&
      this.libraryObject.user.tokensLoaded &&
      // only students need to pay
      this.libraryObject.user.isStudent &&
      this.libraryObject.requiresPayment
    )
  }
}
